define((require) => {
    "use strict";

    var Marionette                                                        = require("marionette"),
        $                                                                 = require("jquery"),
        {
            isString, isFunction, defer, isBoolean, toArray, isArray, each
        }  = require("underscore"),
        {Logger}                                                          = require("shared/util/Logger"),
        SyncManager                                                       = require("shared/services/sync/SyncManager"),
        {isBlank}                                                         = require("shared/util/StringUtils");

    var log = new Logger("ValidateBehavior");

    /**
     * Clears a displayed validation error.
     *
     * @param {Object} $el The jQuery element to clear the validator for, if any; never null.
     * @see   #validateInputField
     */
    function clearValidationError($el) {
        $el.attr("data-original-title", "");

        $el.removeClass("validation-error"); // e.g. Input type="text"
    }

    /**
     *  Returns the value of the element to be validated.
     * @param {Object} $el The jQuery element to be validated, if any; never null.
     * @returns The selected value for the elements. The text for editables fields as they are implemented with DIV tags.
     */
    function val($el) {
        var isSelectElement = $el.is("select");

        if (isSelectElement) {
            return $el.val(); // Selects may have an empty default option, so val should be called.
        }

        return $el.val() || $el.text(); // e.g. text() for editable divs!
    }

    /**
     * Validates an input field.
     *
     * @param {Object}          $el       The jQuery element validated; never null.
     * @param {Function}        validator The validator function like <tt>fn(val:String, type:String)</tt>; never
     *                                    null. The <tt>type</tt> argument to the function is the <tt>type</tt>
     *                                    argument supplied to this function.
     * @param {Function|String} errorText The error text function like <tt>fn(val:String, submit:Boolean)</tt>, or
     *                                    error message as such, i.e. a String. Never null. The <tt>type</tt> argument
     *                                    to the function is the <tt>type</tt> argument supplied to this function.
     * @param {String}          type      The type of validation, e.g. "init" on initial validation on setup, "blur"
     *                                    on validation when <tt>$el</tt> looses focus, or "submit" when the form
     *                                    is about to be submitted. Supports user defined types, e.g. specific ones
     *                                    for specific views.
     * @param {ImprovedModel}   [model]   The model, if any, to have pending sync requests *tried* cancelled on error.
     *
     * @return {{changed:Boolean, value:String}} The (successfully) validated value, where <tt>changed</tt> indicate
     *         a changed value, and <tt>value</tt> will be null if invalid.
     *
     * @see    #clearValidationError
     */
    function validateInputField($el, validator, errorText, type, model, tooltip, showErrorToUser) {
        var value = val($el) || "",
            changed = value !== $el._lastValue,
            erroneous = !changed && $el._erroneous,
            error;
        $el._lastValue = value;

        if (!erroneous && validator(value, type, $el)) {
            // We do not want to trigger a change while going from one valid value, to some invalid value, then
            // back to the same valid value again; only on a different valid value...
            changed = value !== $el._lastValidValue;
            $el._lastValidValue = value;
            $el._erroneous = null;
            clearValidationError($el);

        // Do not report error for same text again, e.g. keyup can be because of meta keys...
        } else if (changed || !$el.hasClass("validation-error")) {
            // Used carried erroneous for consistent error messages...
            if (erroneous) {
                type = erroneous;
            }

            error = isString(errorText) ? errorText : errorText(value, type, $el);

            if (showErrorToUser || type === "submit") {
                $el.addClass("validation-error"); // e.g. Input type="text"
                $el.parent().addClass("validation-error"); // e.g. form

                if (error && tooltip) {
                    $el.attr("data-original-title", error);
                }
            }

            log.verbose("Input validation failed on '" + type + "' ~ '" + value + "' -> <" + error + ">", $el);

            // Model bound to this view only if so configured. If so, cancel any *pending* request, i.e. debounced save.
            // If so, we clear the last successful value to ensure we trigger a change for the valid value again on
            // next successful validation. Better to sync one too many times that one too little...
            if (model && SyncManager.cancel(model, "save")) {
                log.verbose("Cancelling pending sync: " + model, model);
                $el._lastValidValue = null; // only here if actual cancelled...
            }

            value = null;
        } else {
            value = null; // not changed, but still invalid!
        }

        // Set this here as we might coerce into carried erroneous type..
        $el._lastType = type;

        return {changed: changed, value: value};
    }

    /**
     * Setup the tooltip on the validation field
     * This function is responsible to setup the tooltip on the bottom/right of each field.
     *  - The tooltip must to be aligned with the exclamation icon
     *  - The tooltip should disapear once that the user press focus, however
     *  the tooltip should comeback if the user put hover again.
     *  - The position of tooltip should be calculated automatically
     *
     * @param {Object}          [$el]     The jQuery element validated; never null.
     * @param {Function|String} errorText The error text function like <tt>fn(val:String, submit:Boolean)</tt>, or
     *                                    error message as such, i.e. a String. Never null. The <tt>type</tt> argument
     *                                    to the function is the <tt>type</tt> argument supplied to this function.
     */
    function setupTooltip($el, errorText) {
        $el.tooltip({
            placement: "bottom",
            animation: false
        });

        $el.mouseover(() => {
            // Get the STRING message of the errorText
            var error = isString(errorText) ? errorText : errorText(val($el), "mouseover", $el);

            // Check if the error still valid
            if (!isBlank(error)) {
                $el.tooltip("show");
                var $tooltip = $(".tooltip"),
                    leftPos,
                    tooltipWidth = $(".tooltip").width();

                // Offset to the right of the element
                leftPos = $el.position().left + $el.outerWidth() - (tooltipWidth / 2);

                // Align the element in the center of the icon
                if ($el.is("input")) {
                    leftPos -= 20;
                } else {
                    leftPos -= 32;
                }

                $tooltip.css({"left": leftPos + "px", "width": $tooltip.width() + "px"});
            } else {
                $el.tooltip("hide");
            }
        });

        $el.focus(() => {
            $el.tooltip("hide");
        });
    }

    /**
     * @type {*}
     */
    return Marionette.Behavior.extend({

        defaults: {
            isSubmitted: false,

            buttonSubmit: null,

            inputValidation: null,

            isEnabled: true,

            /*
             *  This function if there are still missing fields to be filled in the right way or not.
             *  In case that everything is fine, the button will be green. Otherwise, it will continues to be black.
             */
            applyFormStateBehavior: function() {
                if (isString(this.buttonSubmit)) {
                    var $elButtonSubmit = $(this.buttonSubmit);

                    var isFormValid = true;
                    each(this.inputValidation, (elem) => {
                        isFormValid = elem.isChecked && isFormValid;
                    });

                    if (isFormValid) {
                        $elButtonSubmit.removeClass("btn-not-valid");
                        $elButtonSubmit.addClass("btn-valid");
                    } else {
                        $elButtonSubmit.removeClass("btn-valid");
                        $elButtonSubmit.addClass("btn-not-valid");
                    }
                }
            },

            /**
             * Conditions to the validation be fired
             *
             *  This function will determine when the validation can be fired:
             *      eg: After submit, After submit (key up), After blur, After blur(key up)
             *
             * @param {Object}                             validator The validator of the associated field
             * @param {Function|String} type               Type of event
             * @param {Function|String} validateAfter      String saying if the element wish to be validate before or after submit
             *
             */
            validateTriggerCondition: function(validator, type, validateAfter) {
                if (type === "submit") {
                    this.isSubmitted = true;
                }

                if (type === "blur") {
                    validator.isAlreadyFired = true;
                }

                return (validator.isAlreadyFired || this.isSubmitted || (validateAfter && validateAfter !== "submit"));
            }
        },

        /*
         * Create a validator object with all parameters already initialized in the right way
         *
         *  This function will determine when the validation can be fired:
         *      eg: After submit, After submit (key up), After blur, After blur(key up)
         *
         * @param {Object} $el                            jquery element
         * @param {Object} type                           Type of event

         */
        createObject: function($el, options) {
            var behaviorClass = this,
                validator     = isString(options.validator) ? this[options.validator] : options.validator,
                button        = isFunction(behaviorClass.options.buttonSubmit) ? behaviorClass.options.buttonSubmit : null,
                view          = this.view,
                errorText     = options.errorText;

            if (typeof options.tooltip === "undefined") {
                options.tooltip = true;
            }

            if (isFunction(errorText)) {
                errorText = errorText.bind(view);
            }

            // Maintain scope, e.g. to reference view models from validators and sequential buttons...
            validator = validator.bind(view);

            if (button) {
                button = button.bind(view);
            }

            return {
                $el: $el,
                validator: validator,
                errorText: errorText || "",
                isAlreadyUsed: false,
                options: options || {},
                behaviorClass: behaviorClass,

                /**
                 * Add a SYNC event for the field
                 *
                 * @param {String} [events]        String containing the name of the events that will fire the validation.
                 * @param {Boolean} [hideTooltip]  It should be true, if the tooltip will be hidden.
                 */
                addEvent: function(events, hideTooltip) {
                    if (options.tooltip && hideTooltip) {
                        // Hide the element when you are typing
                        this.$el.tooltip("hide");
                    }
                    // Apply always onblur validate for the fields
                    this.$el.on(events, (event) => {
                        this.handleEvent(event.type);
                    });
                },

                /**
                 * Handle the event
                 *
                 * @param {String} [event]   Type of the event that was fired
                 */
                handleEvent: function(event, recursive) {
                    var showErrorToUser = this.behaviorClass.options.validateTriggerCondition(this.validator, event, this.options.validateAfter),
                        value = validateInputField(this.$el, this.validator, this.errorText, event, this.options.model ? this.behaviorClass.view.model : null, options.tooltip, showErrorToUser);

                    this.options.isChecked = value.value !== null;

                    // Do not trigger a "change" on init or on focus, but on "blur" or any user defined
                    // event if and only if the value is actually changed...
                    if (value.value !== null && value.changed && event !== "init" && event !== "focus") {
                        // This is a *view* change, not a model change. The selector for $el is already
                        // available as $el.selector...
                        this.behaviorClass.view.trigger("change", this.$el, value.value, view);
                    }

                    // Function = SequentialButton enable hook to be called with true for no error, false otherwise. We
                    // have to toggle its state based on all input validation fields, not just the current one...
                    if (!recursive) {
                        if (button) {
                            button(view.inputValidation(event, true));
                        } else if (this.behaviorClass.options.buttonSubmit) {
                            this.behaviorClass.options.applyFormStateBehavior();
                        }
                    }

                    return this.options.isChecked;
                }
            };
        },

        // Collection with all validate objects.
        collectionValidateObjects: [],

        onRender: function() {
            // parameter to disable or enable the ValidateBehavior.
            var isEnabled = isFunction(this.options.isEnabled) ? this.options.isEnabled() : this.options.isEnabled;

            // Handle input validators...
            if (this.options.inputValidation && isEnabled) {
                log.info("Initializing ValidateBehavior");

                this.collectionValidateObjects = [];

                // Once validators have been set up, "inputValidation" will be replaced with a function that
                // will perform the same validation when invoked...
                each(this.options.inputValidation, function(options, element) {
                    var $el = this.$el.find(element);

                    if ($el.length) {
                        // Create a instance of the object
                        var validatorObject = this.createObject($el, options);
                        // Initialize the tooltip, if any...
                        if (validatorObject.errorText && options.tooltip) {
                            setupTooltip($el, validatorObject.errorText);
                        }
                        // Bind the events
                        this.bindEvents(validatorObject);
                        // Save to list of all handleEvents...
                        this.collectionValidateObjects.push(validatorObject);
                    } else {
                        log.warn("No such element to validate: '" + element + "' -> " + this, options);
                    }
                }, this);

                /**
                 * Validates all input fields with configured validators for the specified type, e.g. "submit",
                 * and returns true if all input fields validated successfully, false otherwise.
                 *
                 * @param {String|Boolean} [type]  If a string, the validation type performed, defaults to
                 *                                 "submit". If true, it will give focus to the first element
                 *                                 failing a validation.
                 *
                 *
                 * @return {Boolean} True if all configured input fields could be validated for the specified
                 *                   type, false otherwise.
                 */
                this.view.inputValidation = this.validateAllInputs.bind(this);

                // Once that the page starts will do the first validation
                defer(() => {
                    // Fire the first time
                    this.validateAllInputs("init");
                });
            }
        },
        /**
         * This function will be called for the view. It will fire all validators and check if is everything ok.
         *
         * @param {Boolean} [type]      If it's a boolean we replace that for a submit event
         * @param {Boolean} [recursive] True if validation is called recursive, false otherwise (default).
         */
        validateAllInputs: function(type, recursive) {
            if (isBoolean(type)) {
                type = null;
            }

            var i,
                allOk = true;

            for (i = 0; i < this.collectionValidateObjects.length; i++) {
                allOk = this.collectionValidateObjects[i].handleEvent(type || "submit", recursive) && allOk;
            }

            return allOk;
        },

        /**
         * Bind (Sync/Async) events to the input
         *
         * @param {Object} [validatorObject]  The validator object with all settings of the field
         */
        bindEvents: function(validatorObject) {
            // Async validator(s)?
            if (validatorObject.options.validatorAsync) {
                each(validatorObject.options.validatorAsync, (asyncOptions, event) => {
                    this.view.listenTo(asyncOptions.source, event, function() {
                        // Carry async validation state over to normal validators on no change
                        // in input, or we will clear error messages generated by asynchronous
                        // validation...

                        var handleAsyncValidator = function() {
                            return asyncOptions.validator.apply(this, args);
                        }.bind(this);

                        var args = toArray(arguments),
                            valid = validateInputField(validatorObject.$el, handleAsyncValidator, validatorObject.errorText, "async", validatorObject.options.model ? this.model : null, validatorObject.options.tooltip);

                        validatorObject.$el._erroneous = valid ? null : "async";
                    });
                });
            }

            // Sync Validators
            // Add custom events, if any the function has received one different like blur
            if (validatorObject.options.events) {
                validatorObject.options.events = isArray(validatorObject.options.events) ? validatorObject.options.events.join(" ") : validatorObject.options.events;
                validatorObject.addEvent(validatorObject.options.events);
            } else {
                if (validatorObject.$el.is("select")) {
                    // Validate whenever the user change the field
                    validatorObject.addEvent("change");
                } else {
                    validatorObject.addEvent("keyup", true);
                }
                // Apply always onblur validate for the fields
                validatorObject.addEvent("blur", true);
            }
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/behaviors/ValidateBehavior.js